import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { statusMeta, fmtDate } from "../lib/constants";
import LeadDrawer from "../components/LeadDrawer";
import { Button } from "../components/ui/button";
import { Inbox as InboxIcon, MessageSquare, User } from "lucide-react";

export default function Inbox() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [scope, setScope] = useState(user?.role === "admin" ? "all" : "mine");
  const [drawerId, setDrawerId] = useState(null);

  const { data: convos = [] } = useQuery({
    queryKey: ["inbox", scope],
    queryFn: () => api.get("/inbox", { params: { mine: scope === "mine" } }).then((r) => r.data),
    refetchInterval: 15000,
  });

  const open = async (c) => {
    setDrawerId(c.id);
    if (c.unread) {
      await api.post(`/inbox/${c.id}/read`);
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["unread-count"] });
    }
  };

  return (
    <div data-testid="inbox-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Reply Inbox</h1>
          <p className="text-muted-foreground mt-1">Every lead that texts back — newest first. Unread replies are highlighted.</p>
        </div>
        <div className="flex rounded-lg border border-border p-1 bg-card">
          {["mine", "all"].map((v) => (
            <button
              key={v}
              onClick={() => setScope(v)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${scope === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`inbox-scope-${v}`}
            >
              {v === "mine" ? "My replies" : "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {convos.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-16 text-center text-muted-foreground">
            <InboxIcon className="h-10 w-10 mx-auto mb-3 text-primary" />
            No replies yet. When a lead texts back mid-drip, it lands here.
          </div>
        )}
        {convos.map((c) => {
          const meta = statusMeta(c.status);
          return (
            <button
              key={c.id}
              onClick={() => open(c)}
              className={`w-full text-left rounded-xl border p-4 flex items-center gap-4 transition-colors ${c.unread ? "border-primary/40 bg-primary/5" : "border-border bg-card hover:bg-secondary/40"}`}
              data-testid={`inbox-item-${c.id}`}
            >
              <div className="relative">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-sm font-semibold uppercase">
                  {(c.name || "?").slice(0, 2)}
                </div>
                {c.unread && <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-primary ring-2 ring-card" data-testid={`unread-dot-${c.id}`} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`truncate ${c.unread ? "font-semibold" : "font-medium"}`}>{c.name}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${meta.bg} ${meta.text}`}>{meta.label}</span>
                </div>
                <p className="text-sm text-muted-foreground truncate flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> {c.last_inbound_text}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-muted-foreground">{fmtDate(c.last_inbound_at)}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end mt-1">
                  <User className="h-3 w-3" /> {c.assigned_to ? c.assigned_to.split("@")[0] : "unassigned"}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      <LeadDrawer leadId={drawerId} open={!!drawerId} onOpenChange={(v) => !v && setDrawerId(null)} defaultTab="sms" />
    </div>
  );
}
